import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { apiClient } from "@/lib/api/client";
import { UserInfo } from "@/lib/types/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { User, Lock, Mail, Save, Key, Sun, Moon, Palette, Sparkles, CircleDot } from "lucide-react";
import toast from "react-hot-toast";
import { handleApiError } from "@/lib/utils/errors";
import { useAuth } from "@/lib/contexts/auth-context";
import { useTheme } from "@/lib/contexts/theme-context";

const profileSchema = z.object({
  firstName: z.string().min(1, "First name is required").max(50),
  lastName: z.string().min(1, "Last name is required").max(50),
  email: z.string().email("Invalid email address"),
});

const passwordSchema = z.object({
  currentPassword: z.string().min(1, "Current password is required"),
  newPassword: z.string().min(8, "Password must be at least 8 characters"),
  confirmPassword: z.string().min(1, "Please confirm your password"),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
});

type ProfileFormData = z.infer<typeof profileSchema>;
type PasswordFormData = z.infer<typeof passwordSchema>;

// Helper function to update theme preference in localStorage
const updateUserTheme = async (newTheme: string) => {
  try {
    await apiClient.updateProfile({ selectedTheme: newTheme });
    const userInfo = localStorage.getItem("userInfo");
    if (userInfo) {
      const parsed = JSON.parse(userInfo);
      parsed.selectedTheme = newTheme;
      localStorage.setItem("userInfo", JSON.stringify(parsed));
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error("Failed to save theme preference:", error);
    }
  }
};

export default function SettingsPage() {
  const { user: authUser } = useAuth();
  const { theme, toggleTheme, setTheme } = useTheme();
  const [user, setUser] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Theme change handler
  const handleThemeChange = async (newTheme: "light" | "dark" | "colorful" | "yellow" | "pink" | "orange" | "blue" | "red" | "green") => {
    setTheme(newTheme);
    await updateUserTheme(newTheme);
  };

  const {
    register: registerProfile,
    handleSubmit: handleSubmitProfile,
    reset: resetProfile,
    formState: { errors: profileErrors, isSubmitting: isSubmittingProfile },
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
  });

  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    reset: resetPassword,
    formState: { errors: passwordErrors, isSubmitting: isSubmittingPassword },
  } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.getProfile();
      if (response.success && response.data) {
        setUser(response.data);
        resetProfile({
          firstName: response.data.firstName,
          lastName: response.data.lastName,
          email: response.data.email,
        });
      }
    } catch (error) {
      toast.error(handleApiError(error, "Failed to load profile"));
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmitProfile = async (data: ProfileFormData) => {
    try {
      const response = await apiClient.updateProfile(data);
      if (response.success) {
        toast.success("Profile updated successfully");
        await fetchProfile();
      }
    } catch (error) {
      toast.error(handleApiError(error, "Failed to update profile"));
    }
  };

  const onSubmitPassword = async (data: PasswordFormData) => {
    try {
      const response = await apiClient.changePassword(data);
      if (response.success) {
        toast.success("Password changed successfully");
        resetPassword({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
      } else {
        toast.error(response.message || "Failed to change password");
      }
    } catch (error) {
      toast.error(handleApiError(error, "Failed to change password"));
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-blue-500/10 rounded-lg">
            <User className="h-8 w-8 text-blue-500" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 colorful:text-gray-900 yellow:text-[#78350f] pink:text-[#831843] orange:text-[#7c2d12] blue:text-[#1e3a8a] red:text-[#7f1d1d] green:text-[#14532d]">Settings</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">Manage your profile and security settings</p>
          </div>
        </div>
        <Card>
          <CardContent className="p-8">
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="p-3 bg-blue-500/10 rounded-lg">
          <User className="h-8 w-8 text-blue-500" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 colorful:text-gray-900 yellow:text-[#78350f] pink:text-[#831843] orange:text-[#7c2d12] blue:text-[#1e3a8a] red:text-[#7f1d1d] green:text-[#14532d]">Settings</h1>
          <p className="text-gray-600 dark:text-gray-400 colorful:text-gray-700 yellow:text-[#78350f]/80 pink:text-[#831843]/80 orange:text-[#7c2d12]/80 blue:text-[#1e3a8a]/80 red:text-[#7f1d1d]/80 green:text-[#14532d]/80 mt-1">Manage your profile and security settings</p>
        </div>
      </div>
      {/* Profile Information */}
      <Card>
        <CardHeader className="border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/10 rounded-lg">
              <User className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <CardTitle>Profile Information</CardTitle>
              <CardDescription className="mt-1">Update your personal information</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmitProfile(onSubmitProfile)} className="space-y-5">
            {/* User Details (Read-only) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-100 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 colorful:text-gray-700 yellow:text-[#78350f]/70 pink:text-[#831843]/70 orange:text-[#7c2d12]/70 blue:text-[#1e3a8a]/70 red:text-[#7f1d1d]/70 green:text-[#14532d]/70 mb-1">Username</label>
                <p className="text-sm text-gray-900 dark:text-gray-200 colorful:text-gray-900 yellow:text-[#78350f] pink:text-[#831843] orange:text-[#7c2d12] blue:text-[#1e3a8a] red:text-[#7f1d1d] green:text-[#14532d]">{user?.username}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 colorful:text-gray-700 yellow:text-[#78350f]/70 pink:text-[#831843]/70 orange:text-[#7c2d12]/70 blue:text-[#1e3a8a]/70 red:text-[#7f1d1d]/70 green:text-[#14532d]/70 mb-1">Role</label>
                <p className="text-sm">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                    user?.roles.includes("SuperAdmin")
                      ? "bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300 border border-purple-200 dark:border-purple-700"
                      : user?.roles.includes("Admin")
                      ? "bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 border border-blue-200 dark:border-blue-700"
                      : "bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-700"
                  }`}>
                    {user?.roles[0] || "User"}
                  </span>
                </p>
              </div>
              {user?.districtName && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 colorful:text-gray-700 yellow:text-[#78350f]/70 pink:text-[#831843]/70 orange:text-[#7c2d12]/70 blue:text-[#1e3a8a]/70 red:text-[#7f1d1d]/70 green:text-[#14532d]/70 mb-1">District</label>
                  <p className="text-sm text-gray-900 dark:text-gray-200 colorful:text-gray-900 yellow:text-[#78350f] pink:text-[#831843] orange:text-[#7c2d12] blue:text-[#1e3a8a] red:text-[#7f1d1d] green:text-[#14532d]">{user.districtName}</p>
                </div>
              )}
              {user?.blockName && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 colorful:text-gray-700 yellow:text-[#78350f]/70 pink:text-[#831843]/70 orange:text-[#7c2d12]/70 blue:text-[#1e3a8a]/70 red:text-[#7f1d1d]/70 green:text-[#14532d]/70 mb-1">Block</label>
                  <p className="text-sm text-gray-900 dark:text-gray-200 colorful:text-gray-900 yellow:text-[#78350f] pink:text-[#831843] orange:text-[#7c2d12] blue:text-[#1e3a8a] red:text-[#7f1d1d] green:text-[#14532d]">{user.blockName}</p>
                </div>
              )}
            </div>

            {/* Editable Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="First Name"
                type="text"
                placeholder="John"
                error={profileErrors.firstName?.message}
                {...registerProfile("firstName")}
              />
              <Input
                label="Last Name"
                type="text"
                placeholder="Doe"
                error={profileErrors.lastName?.message}
                {...registerProfile("lastName")}
              />
            </div>

            <Input
              label="Email Address"
              type="email"
              placeholder="john.doe@example.com"
              error={profileErrors.email?.message}
              {...registerProfile("email")}
            />

            <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button type="submit" disabled={isSubmittingProfile} className="gap-2">
                <Save className="w-4 h-4" />
                {isSubmittingProfile ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card>
        <CardHeader className="border-b border-gray-700 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-500/10 rounded-lg">
              <Lock className="h-5 w-5 text-orange-500" />
            </div>
            <div>
              <CardTitle>Change Password</CardTitle>
              <CardDescription className="mt-1">Update your password to keep your account secure</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmitPassword(onSubmitPassword)} className="space-y-5">
            <Input
              label="Current Password"
              type="password"
              placeholder="Enter your current password"
              error={passwordErrors.currentPassword?.message}
              {...registerPassword("currentPassword")}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="New Password"
                type="password"
                placeholder="Enter new password (min 8 characters)"
                error={passwordErrors.newPassword?.message}
                {...registerPassword("newPassword")}
              />
              <Input
                label="Confirm New Password"
                type="password"
                placeholder="Re-enter new password"
                error={passwordErrors.confirmPassword?.message}
                {...registerPassword("confirmPassword")}
              />
            </div>

            <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button type="submit" disabled={isSubmittingPassword} variant="outline" className="gap-2">
                <Lock className="w-4 h-4" />
                {isSubmittingPassword ? "Changing..." : "Change Password"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Theme Settings */}
      <Card>
        <CardHeader className="border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-500/10 rounded-lg">
              <Palette className="h-5 w-5 text-purple-500" />
            </div>
            <div>
              <CardTitle>Appearance</CardTitle>
              <CardDescription className="mt-1">Customize the look and feel of your dashboard</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 colorful:text-gray-800 yellow:text-[#78350f] pink:text-[#831843] orange:text-[#7c2d12] blue:text-[#1e3a8a] red:text-[#7f1d1d] green:text-[#14532d] mb-3">
                Theme Mode <span className="text-xs text-gray-500 dark:text-gray-400 colorful:text-gray-600 yellow:text-[#78350f]/60 pink:text-[#831843]/60 orange:text-[#7c2d12]/60 blue:text-[#1e3a8a]/60 red:text-[#7f1d1d]/60 green:text-[#14532d]/60">(Current: {theme})</span>
              </label>
              
              {/* Base Themes */}
              <div className="mb-4">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 colorful:text-gray-600 yellow:text-[#78350f]/60 pink:text-[#831843]/60 orange:text-[#7c2d12]/60 blue:text-[#1e3a8a]/60 red:text-[#7f1d1d]/60 green:text-[#14532d]/60 mb-2">Base Themes</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Button
                    type="button"
                    variant={theme === "light" ? "default" : "outline"}
                    className="flex-1 gap-2"
                    onClick={() => handleThemeChange("light")}
                  >
                    <Sun className="h-4 w-4" />
                    Light
                  </Button>
                  <Button
                    type="button"
                    variant={theme === "dark" ? "default" : "outline"}
                    className="flex-1 gap-2"
                    onClick={() => handleThemeChange("dark")}
                  >
                    <Moon className="h-4 w-4" />
                    Dark
                  </Button>
                  <Button
                    type="button"
                    variant={theme === "colorful" ? "default" : "outline"}
                    className="flex-1 gap-2"
                    onClick={() => handleThemeChange("colorful")}
                  >
                    <Sparkles className="h-4 w-4" />
                    Colorful
                  </Button>
                </div>
              </div>

              {/* Color Themes */}
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 colorful:text-gray-600 yellow:text-[#78350f]/60 pink:text-[#831843]/60 orange:text-[#7c2d12]/60 blue:text-[#1e3a8a]/60 red:text-[#7f1d1d]/60 green:text-[#14532d]/60 mb-2">Color Themes</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <Button
                    type="button"
                    variant={theme === "yellow" ? "default" : "outline"}
                    className="flex-1 gap-2"
                    onClick={() => handleThemeChange("yellow")}
                  >
                    <CircleDot className="h-4 w-4" style={{ color: '#fbb615' }} />
                    Yellow
                  </Button>
                  <Button
                    type="button"
                    variant={theme === "pink" ? "default" : "outline"}
                    className="flex-1 gap-2"
                    onClick={() => handleThemeChange("pink")}
                  >
                    <CircleDot className="h-4 w-4" style={{ color: '#df1b82' }} />
                    Pink
                  </Button>
                  <Button
                    type="button"
                    variant={theme === "orange" ? "default" : "outline"}
                    className="flex-1 gap-2"
                    onClick={() => handleThemeChange("orange")}
                  >
                    <CircleDot className="h-4 w-4" style={{ color: '#f36f26' }} />
                    Orange
                  </Button>
                  <Button
                    type="button"
                    variant={theme === "blue" ? "default" : "outline"}
                    className="flex-1 gap-2"
                    onClick={() => handleThemeChange("blue")}
                  >
                    <CircleDot className="h-4 w-4" style={{ color: '#057dbb' }} />
                    Blue
                  </Button>
                  <Button
                    type="button"
                    variant={theme === "red" ? "default" : "outline"}
                    className="flex-1 gap-2"
                    onClick={() => handleThemeChange("red")}
                  >
                    <CircleDot className="h-4 w-4" style={{ color: '#c22034' }} />
                    Red
                  </Button>
                  <Button
                    type="button"
                    variant={theme === "green" ? "default" : "outline"}
                    className="flex-1 gap-2"
                    onClick={() => handleThemeChange("green")}
                  >
                    <CircleDot className="h-4 w-4" style={{ color: '#3ead49' }} />
                    Green
                  </Button>
                </div>
              </div>

              <p className="mt-3 text-xs text-gray-500 dark:text-gray-400 colorful:text-gray-600 yellow:text-[#78350f]/60 pink:text-[#831843]/60 orange:text-[#7c2d12]/60 blue:text-[#1e3a8a]/60 red:text-[#7f1d1d]/60 green:text-[#14532d]/60">
                Choose from 9 different themes to personalize your dashboard experience.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
